/**
 * Global app scale.
 *
 * The interface is drawn for roughly 80% of browser-default sizing — at 100%
 * browser zoom the type, icons and cards read as oversized. Rather than
 * rewriting the ~1750 hardcoded px values scattered through the pages, the
 * whole document is scaled, which is exactly what browser zoom does.
 *
 * `zoom` is used rather than `transform: scale()` on purpose: `transform`
 * establishes a containing block for `position: fixed` descendants, which would
 * break the fixed Sidenav drawer and every modal portal.
 *
 * Overlay side effect: MUI Popper/Popover mix zoomed getBoundingClientRect()
 * with unzoomed CSS top/left. installCssZoomOverlayFix() in cssZoomOverlays.js
 * corrects that for every Select / Autocomplete / Tooltip under body zoom.
 *
 * Shared by the light and dark theme globals so the two cannot drift.
 */

// Keep in sync with ZoomControls in examples/Navbars/DashboardNavbar.
export const APP_SCALE = 0.8;

// Scale only from the theme's `lg` breakpoint up. Below it the app uses its
// mobile layout, where 80% would push body text to ~11px and shrink touch
// targets below the 44px guideline.
export const APP_SCALE_MIN_WIDTH = 992;

/**
 * Effective CSS `zoom` on <body>.
 *
 * Chrome reports zoomed getBoundingClientRect() values, but style top/left and
 * Popper transforms are applied in unzoomed CSS pixels and then zoomed again.
 * Overlays must divide by this factor or they drift (tooltips, Selects, Menus).
 *
 * Returns 1 when zoom is unset / "normal".
 */
export function getCssZoom() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 1;
  }
  const { body } = document;
  if (!body) return 1;

  const raw = window.getComputedStyle(body).zoom;
  if (raw && raw !== "normal") {
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed > 3 ? parsed / 100 : parsed;
    }
  }

  if (body.style.zoom) {
    const inline = parseFloat(body.style.zoom);
    if (Number.isFinite(inline) && inline > 0) {
      return inline > 3 ? inline / 100 : inline;
    }
  }

  // Last resort: visual vs layout size of a body child (MessagesPage technique).
  const probe = document.getElementById("app") || body.firstElementChild;
  if (probe && probe.offsetWidth > 0) {
    const ratio = probe.getBoundingClientRect().width / probe.offsetWidth;
    if (Number.isFinite(ratio) && ratio > 0.05 && ratio < 10 && Math.abs(ratio - 1) > 0.02) {
      return ratio;
    }
  }

  return 1;
}

/**
 * Viewport heights resolve against the unscaled viewport, so anything sized in
 * `vh` has to be divided by the scale to still fill the screen.
 */
// The divisor is a CSS variable so UI v2, which renders without the zoom,
// can set `--app-scale: 1` on <body> and get plain 100vh/100vw. Legacy never
// sets it, so the fallback keeps its output identical.
const SCALE = `var(--app-scale, ${APP_SCALE})`;

// Height the v2 frame takes away from a full-height page (header and the
// padding of its scrolling <main>); AppFrame sets it on <main>. Unset
// everywhere else, so `100vh - 0px` and classic sizes are unchanged.
const VIEWPORT_HEIGHT = "(100vh - var(--v2-frame-offset, 0px))";

export const scaledVh = (subtract) =>
  subtract
    ? `calc(${VIEWPORT_HEIGHT} / ${SCALE} - ${subtract})`
    : `calc(${VIEWPORT_HEIGHT} / ${SCALE})`;

export const scaledVw = (subtract) =>
  subtract
    ? `calc(100vw / ${SCALE} - ${subtract})`
    : `calc(100vw / ${SCALE})`;

/**
 * Full-viewport sizing for a layout root, applied only where the scale is
 * active so narrow screens keep plain 100vw/100vh.
 *
 * Only needed for in-flow elements. `position: fixed` elements (the Sidenav
 * drawer, modals) resolve viewport units against the scaled containing block
 * already and must be left alone.
 */
export const fullViewportSx = {
  width: "100vw",
  minHeight: "100vh",
  [`@media screen and (min-width: ${APP_SCALE_MIN_WIDTH}px)`]: {
    width: scaledVw(),
    minHeight: scaledVh(),
  },
};

/** Full viewport height only, for page frames that manage their own width. */
export const fullHeightSx = {
  minHeight: "100vh",
  [`@media screen and (min-width: ${APP_SCALE_MIN_WIDTH}px)`]: {
    minHeight: scaledVh(),
  },
};

// NOTE: `screen and` is load-bearing, not decoration. The theme builds
// CssBaseline as `{...globals, ...container}`, and container.js already has a
// plain `@media (min-width: 992px)` key — an identical key here would be
// silently dropped by the object spread. Restricting to `screen` also keeps
// printed output at full size.
export const appScaleGlobals = {
  [`@media screen and (min-width: ${APP_SCALE_MIN_WIDTH}px)`]: {
    body: {
      zoom: APP_SCALE,
    },
    "#app": {
      minHeight: scaledVh(),
    },
  },
};

export default appScaleGlobals;
